import authService from './authService';
import { DeviceStats, HistorySettings, Machine, MachineHistorySeries, Notification, UserProfile } from '../types';
import { downloadCsv } from '../utils/csvExport';

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

interface UserToken {
  id: number;
  token_type: string;
  name: string;
  created_at: string;
  expires_at: string;
  last_used_at?: string;
  scopes: string[];
}

interface NotificationResponse {
  notifications: Notification[];
  total_count: number;
  unread_count: number;
  read_count: number;
  last_updated: string;
}

class ApiService {
  private baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
  private authBaseURL = process.env.REACT_APP_AUTH_BASE_URL || 'http://localhost:8000/api/auth';

  // Generic fetch method with comprehensive auth handling
  private async fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      // Check if user is authenticated
      const isAuth = await authService.isAuthenticated();
      if (!isAuth) {
        throw new Error('User not authenticated');
      }

      const authHeader = authService.getAuthHeader();
      if (!authHeader) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
          ...options.headers,
        },
      });

      // Handle 401 specifically - token might be expired
      if (response.status === 401) {
        const refreshed = await authService.refreshToken();
        if (refreshed) {
          // Retry the request with new token
          const newAuthHeader = authService.getAuthHeader();
          if (newAuthHeader) {
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': newAuthHeader,
                ...options.headers,
              },
            });

            if (retryResponse.ok) {
              const data = await retryResponse.json();
              return { data, success: true };
            }
          }
        }
        
        authService.logout();
        throw new Error('Authentication failed - please login again');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'No error details provided.' }));
        let errorMessage = errorData.detail || errorData.error || errorData.message;
        if (!errorMessage) {
          // DRF field-level validation errors: { field: ["msg", ...], ... }
          const fieldErrors = Object.entries(errorData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
          errorMessage = fieldErrors || `HTTP error! status: ${response.status}`;
        }
        console.error('API call failed:', errorMessage, 'Status:', response.status);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, success: true };
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return { 
        data: null as T, 
        success: false, 
        error: errorMessage
      };
    }
  }

  // Authentication-related API calls
  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    return this.fetchWithAuth<UserProfile>(`${this.authBaseURL}/profile/`);
  }

  async getUserTokens(): Promise<ApiResponse<{ tokens: UserToken[] }>> {
    return this.fetchWithAuth<{ tokens: UserToken[] }>(`${this.authBaseURL}/tokens/`);
  }

  async revokeToken(tokenId: number): Promise<ApiResponse<{ message: string }>> {
    return this.fetchWithAuth<{ message: string }>(`${this.authBaseURL}/tokens/${tokenId}/`, {
      method: 'DELETE',
    });
  }

  async revokeAllTokens(): Promise<ApiResponse<{ message: string }>> {
    return this.fetchWithAuth<{ message: string }>(`${this.authBaseURL}/tokens/`, {
      method: 'DELETE',
    });
  }

  async validateToken(token: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.authBaseURL}/token/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        return { data, success: true };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return { 
          data: null, 
          success: false, 
          error: errorData.error || 'Token validation failed' 
        };
      }
    } catch (error) {
      return { 
        data: null, 
        success: false, 
        error: error instanceof Error ? error.message : 'Network error' 
      };
    }
  }

  // Service stats API calls
  async getDeviceStats(params?: {
    site_id?: string;
    client_id?: string;
    device_type?: string;
    limit?: number;
    start_date?: string;
    end_date?: string;
    active_since?: string;
    stale_before?: string;
  }): Promise<ApiResponse<DeviceStats[]>> {
    const queryParams = new URLSearchParams();
    if (params?.site_id) queryParams.append('site_id', params.site_id);
    if (params?.client_id) queryParams.append('client_id', params.client_id);
    if (params?.device_type) queryParams.append('device_type', params.device_type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.active_since) queryParams.append('active_since', params.active_since);
    if (params?.stale_before) queryParams.append('stale_before', params.stale_before);

    const url = `${this.baseURL}/service-stats/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.fetchWithAuth<DeviceStats[]>(url);
  }

  // Full detail for an exact list of site_ids — used for group-aware table
  // pagination, where the caller has already decided which sites (whole
  // Project groups) belong on the current page.
  async getDeviceStatsByIds(siteIds: string[]): Promise<ApiResponse<DeviceStats[]>> {
    if (siteIds.length === 0) {
      return { success: true, data: [] };
    }
    const url = `${this.baseURL}/service-stats/?site_ids=${encodeURIComponent(siteIds.join(','))}`;
    return this.fetchWithAuth<DeviceStats[]>(url);
  }

  // Lightweight variant excluding stats/additional_notes/vitals_metrics — for
  // counts and filter dropdowns across every device without the heavy JSON cost.
  async getDeviceStatsSummary(params?: {
    site_id?: string;
    client_id?: string;
  }): Promise<ApiResponse<DeviceStats[]>> {
    const queryParams = new URLSearchParams();
    queryParams.append('summary', 'true');
    if (params?.site_id) queryParams.append('site_id', params.site_id);
    if (params?.client_id) queryParams.append('client_id', params.client_id);

    const url = `${this.baseURL}/service-stats/?${queryParams.toString()}`;
    return this.fetchWithAuth<DeviceStats[]>(url);
  }

  // Real backend pagination for the device stats table — only fetches full
  // per-device detail (including stats/additional_notes) for the current page.
  async getDeviceStatsPage(params: {
    page: number;
    page_size?: number;
    site_id?: string;
    client_id?: string;
    device_type?: string;
    active_since?: string;
    stale_before?: string;
  }): Promise<ApiResponse<{ count: number; page: number; page_size: number; total_pages: number; results: DeviceStats[] }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    if (params.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params.site_id) queryParams.append('site_id', params.site_id);
    if (params.client_id) queryParams.append('client_id', params.client_id);
    if (params.device_type) queryParams.append('device_type', params.device_type);
    if (params.active_since) queryParams.append('active_since', params.active_since);
    if (params.stale_before) queryParams.append('stale_before', params.stale_before);

    const url = `${this.baseURL}/service-stats/?${queryParams.toString()}`;
    return this.fetchWithAuth(url);
  }

  // Server-side sums for the dashboard's Processing Metrics cards — avoids
  // shipping every device's full stats JSON to the browser just to add it up.
  async getProcessingMetrics(params?: {
    site_id?: string;
    client_id?: string;
    device_type?: string;
  }): Promise<ApiResponse<{
    total_images_synced: number;
    total_images_processed: number;
    total_images_received: number;
    total_tasks_processed: number;
    total_tasks_received: number;
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.site_id) queryParams.append('site_id', params.site_id);
    if (params?.client_id) queryParams.append('client_id', params.client_id);
    if (params?.device_type) queryParams.append('device_type', params.device_type);

    const url = `${this.baseURL}/service-stats-metrics/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.fetchWithAuth(url);
  }

  async getDeviceStatsFiltered(filters: {
    start_date?: string;
    end_date?: string;
    site_id?: string;
    client_id?: string;
  }): Promise<ApiResponse<DeviceStats[]>> {
    return this.fetchWithAuth<DeviceStats[]>(`${this.baseURL}/service-stats/`, {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  async submitDeviceData(data: {
    site_id: string;
    client_information: any;
    mac_address: string;
    stat_json: any;
  }): Promise<ApiResponse<{ message: string; id: number }>> {
    return this.fetchWithAuth<{ message: string; id: number }>(`${this.baseURL}/data/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Machine (trial license) API calls
  async getMachines(params?: {
    serial_number?: string;
    activation_status?: 'trial' | 'activated';
  }): Promise<ApiResponse<Machine[]>> {
    const queryParams = new URLSearchParams();
    if (params?.serial_number) queryParams.append('serial_number', params.serial_number);
    if (params?.activation_status) queryParams.append('activation_status', params.activation_status);

    const url = `${this.baseURL}/machines/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.fetchWithAuth<Machine[]>(url);
  }

  // --- Stats history (Analytics page) ---

  /** Default captured metrics plus every machine currently recording history. */
  async getHistorySettings(): Promise<ApiResponse<HistorySettings>> {
    return this.fetchWithAuth<HistorySettings>(`${this.baseURL}/history-settings/`);
  }

  /** Time series for one machine, already bucketed and counter-adjusted. */
  async getMachineHistory(
    serialNumber: string,
    params?: {
      hours?: number;
      days?: number;
      from?: string;
      to?: string;
      /** raw | 15m | 30m | 1h | 3h | 6h | 12h | day | week | month | minutes | auto */
      interval?: string;
      /** max | avg | min | last | first | sum | count */
      aggregation?: string;
      /** total (running total as stored) | delta (change per bucket) */
      counter_view?: string;
      metrics?: string[];
      points?: number;
    },
  ): Promise<ApiResponse<MachineHistorySeries>> {
    const q = new URLSearchParams();
    if (params?.hours) q.append('hours', String(params.hours));
    if (params?.days) q.append('days', String(params.days));
    if (params?.from) q.append('from', params.from);
    if (params?.to) q.append('to', params.to);
    if (params?.interval) q.append('interval', params.interval);
    if (params?.aggregation) q.append('aggregation', params.aggregation);
    if (params?.counter_view) q.append('counter_view', params.counter_view);
    if (params?.points) q.append('points', String(params.points));
    if (params?.metrics?.length) q.append('metrics', params.metrics.join(','));
    const qs = q.toString();
    return this.fetchWithAuth<MachineHistorySeries>(
      `${this.baseURL}/machines/${encodeURIComponent(serialNumber)}/history/${qs ? '?' + qs : ''}`,
    );
  }

  /**
   * Update a machine's history settings. Only the fields passed are changed,
   * so toggling does not reset retention.
   */
  async updateMachineHistory(
    serialNumber: string,
    settings: { history_enabled?: boolean; history_retention_count?: number },
  ): Promise<ApiResponse<Machine>> {
    return this.fetchWithAuth<Machine>(`${this.baseURL}/machines/`, {
      method: 'PATCH',
      body: JSON.stringify({ serial_number: serialNumber, ...settings }),
    });
  }

  // Get aggregated stats by site
  async getStatsBySite(): Promise<ApiResponse<any>> {
    const response = await this.getDeviceStats();
    if (response.success) {
      // Client-side aggregation for now
      const siteStats = this.aggregateStatsBySite(response.data);
      return { data: siteStats, success: true };
    }
    return response;
  }

  // Get unique sites and clients for filters
  async getUniqueValues(): Promise<ApiResponse<{ sites: string[]; clients: string[] }>> {
    const response = await this.getDeviceStats({ limit: 1000 });
    if (response.success) {
      const sites = Array.from(new Set(response.data.map(stat => stat.site_id))).sort();
      const clients = Array.from(new Set(response.data.map(stat => stat.client_id))).sort();
      return { data: { sites, clients }, success: true };
    }
    return { data: { sites: [], clients: [] }, success: false, error: response.error };
  }

  // Get dashboard metrics
  async getDashboardMetrics(filters?: {
    start_date?: string;
    end_date?: string;
    site_id?: string;
    client_id?: string;
  }): Promise<ApiResponse<{
    totalSites: number;
    activeClients: number;
    syncStatus: number;
    tasksCompleted: number;
    imagesProcessed: number;
  }>> {
    const response = await this.getDeviceStats(filters);
    if (response.success) {
      const metrics = this.calculateDashboardMetrics(response.data);
      return { data: metrics, success: true };
    }
    return { 
      data: { totalSites: 0, activeClients: 0, syncStatus: 0, tasksCompleted: 0, imagesProcessed: 0 }, 
      success: false, 
      error: response.error 
    };
  }

  // Export data as CSV
  exportToCsv(data: DeviceStats[], filename: string = 'device_stats.csv'): void {
    const headers = [
      'Batch Index',
      'UUID',
      'Site',
      'Client',
      'Start Time',
      'End Time',
      'Tasks Completed',
      'Images Processed',
      'Processing Time (ms)',
      'Priority',
      'Tags',
      'Processing Notes'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(stat => [
        stat.batch_index,
        stat.uuid,
        stat.site_id,
        stat.client_id,
        stat.start_time,
        stat.end_time,
        stat.stats.tasks_processed_current || stat.stats.tasks_completed || 0,
        stat.stats.images_processed_current || stat.stats.images_processed || 0,
        stat.stats.processing_time_ms || 0,
        stat.additional_notes?.priority || '',
        (stat.additional_notes?.tags || []).join(';'),
        stat.additional_notes?.processing_notes || ''
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    downloadCsv(csvContent, filename);
  }

  // Private helper methods
  private aggregateStatsBySite(data: DeviceStats[]) {
    const siteMap = new Map();
    
    data.forEach(stat => {
      if (!siteMap.has(stat.site_id)) {
        siteMap.set(stat.site_id, {
          site_id: stat.site_id,
          count: 0,
          latest_timestamp: stat.event_received_time,
          clients: new Set(),
          tasks_completed: 0,
          tasks_pending: 0,
          images_processed: 0,
          image_sync_pending: 0,
        });
      }
      
      const site = siteMap.get(stat.site_id);
      site.count++;
      site.clients.add(stat.client_id);
      
      // Aggregate stats - use the correct field names
      site.tasks_completed += stat.stats.tasks_processed_current || stat.stats.tasks_completed || 0;
      site.tasks_pending += stat.stats.tasks_pending_current || stat.stats.tasks_pending || 0;
      site.images_processed += stat.stats.images_processed_current || stat.stats.images_processed || 0;
      site.image_sync_pending += stat.stats.images_sync_pending_current || stat.stats.image_sync_pending || 0;
      
      if (new Date(stat.event_received_time) > new Date(site.latest_timestamp)) {
        site.latest_timestamp = stat.event_received_time;
      }
    });

    return Array.from(siteMap.values()).map(site => ({
      ...site,
      clients: Array.from(site.clients),
    }));
  }

  private calculateDashboardMetrics(data: DeviceStats[]) {
    const uniqueSites = new Set(data.map(stat => stat.site_id));
    const uniqueClients = new Set(data.map(stat => stat.client_id));
    
    const totalTasks = data.reduce((sum, stat) => sum + (stat.stats.tasks_processed_current || stat.stats.tasks_completed || 0), 0);
    const totalImages = data.reduce((sum, stat) => sum + (stat.stats.images_processed_current || stat.stats.images_processed || 0), 0);
    const syncedImages = data.reduce((sum, stat) => sum + (stat.stats.patients_synced_current || 0), 0);
    
    return {
      totalSites: uniqueSites.size,
      activeClients: uniqueClients.size,
      syncStatus: totalImages > 0 ? Math.round((syncedImages / totalImages) * 100) : 0,
      tasksCompleted: totalTasks,
      imagesProcessed: totalImages,
    };
  }

  // Critical Alerts API methods
  async getCriticalAlerts(filters?: {
    failure_type?: string;
    severity?: string;
    status?: string;
    start_time?: string;
    end_time?: string;
    resolved_date?: string;
    site_id?: string;
    client_id?: string;
    source_type?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          queryParams.append(key, String(value));
        }
      });
    }

    const url = queryParams.toString()
      ? `${this.baseURL}/critical-alerts/alerts/?${queryParams.toString()}`
      : `${this.baseURL}/critical-alerts/alerts/`;

    return this.fetchWithAuth<any[]>(url);
  }

  async getCriticalAlertsSummary(filters?: any): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          queryParams.append(key, value as string);
        }
      });
    }
    
    const url = queryParams.toString()
      ? `${this.baseURL}/critical-alerts/alerts/summary/?${queryParams.toString()}`
      : `${this.baseURL}/critical-alerts/alerts/summary/`;
    
    return this.fetchWithAuth<any>(url);
  }

  async acknowledgeAlert(alertId: string, notes?: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/${alertId}/update_status/`,
      {
        method: 'POST',
        body: JSON.stringify({
          action: 'acknowledge',
          notes: notes || ''
        })
      }
    );
  }

  async resolveAlert(alertId: string, notes?: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/${alertId}/update_status/`,
      {
        method: 'POST',
        body: JSON.stringify({
          action: 'resolve',
          notes: notes || ''
        })
      }
    );
  }

  async reopenAlert(alertId: string, notes?: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/${alertId}/update_status/`,
      {
        method: 'POST',
        body: JSON.stringify({
          action: 'reopen',
          notes: notes || ''
        })
      }
    );
  }

  async archiveAlert(alertId: string, notes?: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/${alertId}/update_status/`,
      {
        method: 'POST',
        body: JSON.stringify({
          action: 'archive',
          notes: notes || ''
        })
      }
    );
  }

  async bulkAlertAction(alertIds: string[], action: 'acknowledge' | 'resolve' | 'reopen' | 'archive', notes?: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/bulk_action/`,
      {
        method: 'POST',
        body: JSON.stringify({
          alert_ids: alertIds,
          action: action,
          notes: notes || ''
        })
      }
    );
  }

  async checkGatewayOffline(): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`${this.baseURL}/critical-alerts/gateway-offline-check/`);
  }

  // Custom Alert Rules API methods
  async getCustomAlertRules(): Promise<ApiResponse<any[]>> {
    const response = await this.fetchWithAuth<any>(`${this.baseURL}/critical-alerts/custom-rules/?page_size=1000`);
    if (response.success) {
      // Handle DRF paginated response { count, results: [...] }
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results ?? []);
      return { ...response, data };
    }
    return { ...response, data: [] };
  }

  async createCustomAlertRule(rule: any): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/`,
      {
        method: 'POST',
        body: JSON.stringify(rule)
      }
    );
  }

  async updateCustomAlertRule(ruleId: string, rule: any): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/${ruleId}/`,
      {
        method: 'PUT',
        body: JSON.stringify(rule)
      }
    );
  }

  async deleteCustomAlertRule(ruleId: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/${ruleId}/`,
      {
        method: 'DELETE'
      }
    );
  }

  async testCustomAlertRule(ruleId: string, testData: any): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/${ruleId}/test_condition/`,
      {
        method: 'POST',
        body: JSON.stringify(testData)
      }
    );
  }

  async bulkActivateRules(ruleIds: string[]): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/bulk_activate/`,
      {
        method: 'POST',
        body: JSON.stringify({ rule_ids: ruleIds })
      }
    );
  }

  async bulkDeactivateRules(ruleIds: string[]): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/custom-rules/bulk_deactivate/`,
      {
        method: 'POST',
        body: JSON.stringify({ rule_ids: ruleIds })
      }
    );
  }

  async createAlert(alertData: {
    alert_type: string;
    title: string;
    description: string;
    severity: string;
    site_id: string;
    client_id?: string;
    service_name?: string;
    error_message?: string;
    count?: number;
    metadata?: any;
  }): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/alerts/`,
      {
        method: 'POST',
        body: JSON.stringify(alertData)
      }
    );
  }

  async createAlertFromServiceStats(serviceStatsData: any): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `${this.baseURL}/critical-alerts/create-from-stats/`,
      {
        method: 'POST',
        body: JSON.stringify(serviceStatsData)
      }
    );
  }

  // Notification API methods
  async getNotifications(params?: {
    limit?: number;
    hours?: number;
    mark_read?: boolean;
  }): Promise<ApiResponse<NotificationResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.hours) queryParams.append('hours', params.hours.toString());
    if (params?.mark_read) queryParams.append('mark_read', params.mark_read.toString());

    const url = `${this.baseURL}/notifications/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.fetchWithAuth<NotificationResponse>(url);
  }

  async markNotificationsAsRead(notificationIds: string[], markAllRead: boolean = false): Promise<ApiResponse<{ message: string; marked_count: number }>> {
    return this.fetchWithAuth<{ message: string; marked_count: number }>(
      `${this.baseURL}/notifications/`,
      {
        method: 'POST',
        body: JSON.stringify({
          notification_ids: notificationIds,
          mark_all_read: markAllRead
        })
      }
    );
  }

  async getUnreadNotificationCount(): Promise<ApiResponse<{ unread_count: number }>> {
    const response = await this.getNotifications({ limit: 1 });
    if (response.success) {
      return { 
        data: { unread_count: response.data.unread_count }, 
        success: true 
      };
    }
    return { 
      data: { unread_count: 0 }, 
      success: false, 
      error: response.error 
    };
  }
}

const apiService = new ApiService();
export default apiService; 